import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const STANDARDS = ["10th", "12th"];
const BOARDS = [
  { value: "KSEEB", label: "KSEEB (Karnataka)" },
  { value: "MSBSHSE", label: "MSBSHSE (Maharashtra)" },
  { value: "CBSE", label: "CBSE" },
];
const MEDIUMS = ["Kannada", "English", "Marathi"];

export default function GoogleProfilePrompt() {
  const { user, completeGoogleProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", standard: "", board: "", medium: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const open = Boolean(user?.role === "student" && user?.profile_complete === false);

  useEffect(() => {
    if (!open) return;
    setForm({
      name: user?.name || "",
      phone: user?.phone || "",
      standard: user?.standard || "",
      board: user?.board || "",
      medium: user?.medium || "",
    });
    setError("");
  }, [open, user]);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim() || !form.standard || !form.board || !form.medium) {
      setError("Please complete every field.");
      return;
    }
    setSaving(true);
    try {
      await completeGoogleProfile(form);
      toast.success("Profile completed");
    } catch (err) {
      const message = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const signOut = () => {
    logout();
    navigate("/login");
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-md [&>button]:hidden"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        data-testid="google-profile-dialog"
      >
        <form onSubmit={submit} className="space-y-5">
          <DialogHeader>
            <DialogTitle>Complete your student profile</DialogTitle>
            <DialogDescription>
              Tell us a few more details so we can show question papers for your board and medium.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="google-profile-name">Full name</Label>
            <Input id="google-profile-name" value={form.name} maxLength={100} autoComplete="name"
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              data-testid="google-profile-name" required autoFocus />
          </div>

          <div className="space-y-2">
            <Label htmlFor="google-profile-phone">Phone number</Label>
            <Input id="google-profile-phone" type="tel" value={form.phone} maxLength={20}
              placeholder="+91 98765 43210" autoComplete="tel"
              onChange={(event) => setForm({ ...form, phone: event.target.value })}
              data-testid="google-profile-phone" required />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="google-profile-board">Board</Label>
              <Select value={form.board} onValueChange={(board) => setForm({ ...form, board })}>
                <SelectTrigger id="google-profile-board" data-testid="google-profile-board"><SelectValue placeholder="Board" /></SelectTrigger>
                <SelectContent>{BOARDS.map(({ value, label }) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="google-profile-standard">Standard</Label>
              <Select value={form.standard} onValueChange={(standard) => setForm({ ...form, standard })}>
                <SelectTrigger id="google-profile-standard" data-testid="google-profile-standard"><SelectValue placeholder="Standard" /></SelectTrigger>
                <SelectContent>{STANDARDS.map((standard) => <SelectItem key={standard} value={standard}>{standard}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="google-profile-medium">Medium</Label>
              <Select value={form.medium} onValueChange={(medium) => setForm({ ...form, medium })}>
                <SelectTrigger id="google-profile-medium" data-testid="google-profile-medium"><SelectValue placeholder="Medium" /></SelectTrigger>
                <SelectContent>{MEDIUMS.map((medium) => <SelectItem key={medium} value={medium}>{medium}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {error && <p className="text-sm text-destructive" data-testid="google-profile-error">{error}</p>}

          <DialogFooter className="gap-2 sm:space-x-0">
            <Button type="button" variant="ghost" onClick={signOut} disabled={saving}>Sign out</Button>
            <Button type="submit" disabled={saving} data-testid="google-profile-submit">
              {saving ? "Saving…" : "Save and continue"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
